
import { Component, Vue, Prop } from 'vue-property-decorator'
import { Client, User, BasicEntity, UserStatus, ClientWorkspacesFilters, Workspace, Group, Role, RoleCatalogFilters, BLUEMESSAGING_ID } from '@/types/Catalogs'
import DateSelector from '@/components/generic/DateSelector.vue'
import LoadingMask from '@/components/generic/LoadingMask.vue'
import AdminGroupSelectionTreeItem from '@/components/admin/AdminGroupSelectionTreeItem.vue'
import catalogsService from '@/services/CatalogsService'
import { ADMIN_ROLES, RoleNames } from '@/types/Auth'
import userInfoStore from '@/store/userInfo'
import { PageRequest } from '@/types/Support'
import phoneCodes from '@/data/phoneCodes'
import messageService from '@/services/support/MessageService'
import { MessageError } from '../generic/MessageHandling.vue'
import { GroupCatalogFilters, GroupStatus } from '@/types/catalogs/Groups'
import DateFormat from '@/services/DateFormat'

@Component({
  components: {
    DateSelector,
    LoadingMask,
    AdminGroupSelectionTreeItem
  }
})
export default class AdminUserForm extends Vue {
  @Prop({ required: true, default: 0 })
  id: number

  @Prop({ required: true, default: '' })
  title: string

  @Prop({ required: false, default: false })
  readOnly: boolean

  @Prop({ required: false, default: false })
  editButton: boolean

  @Prop({ required: false, default: false })
  deleteButton: boolean

  @Prop({ required: false, default: false })
  resetPasswordButton: boolean

  @Prop({ required: true, default: [] })
  domains: BasicEntity[]

  clients: Client[] = []
  groups: Group[] = []
  roles: Role[] = []

  phoneCodes: any[] = []

  loading = false
  loadingClients = false
  loadingRoles = false

  form: User = User.getIntanceForEdit()

  statusOptions: any[] = []

  regexUsername = /^[0-9a-zA-Z_-]{3,50}$/
  regexPhone = /^[0-9]{10,11}$/

  creationDate = ''
  updateDate = ''
  disableDate = ''

  domainSelectionOpen = false
  domainSelected: any = {}
  domainSelectedBackup: any = {}
  searchDomain = ''

  clientSelectionOpen = false
  clientSelected: any = {}
  clientSelectedBackup: any = {}
  searchClient = ''

  roleSelectionOpen = false
  roleSelected: any = {}
  roleSelectedBackup: any = {}
  searchRole = ''

  countryFlag = ''
  selectedCountryIso = ''

  watchGroupsTreeActive = false
  BMAdminWithPermissionRoles: RoleNames[] = [
    RoleNames.OPERATIONS_ADMIN,
    RoleNames.SUPPORT
  ]

  created () {
    this.phoneCodes = [{ value: null, text: '-- Seleccione código de país --', iso: '' }].concat(
      phoneCodes.reduce((arr: any[], it: any) => arr.concat({ value: `+${it.phone_code}`, text: `${it.nombre} (+${it.phone_code})`, iso: it.iso2.toLowerCase() }), [])
    ) as any
  }

  mounted () {
    this.init()
  }

  init () {
    if (this.id) {
      this.loading = true
      catalogsService.getUserById(this.id).then(async (data: User) => {
        this.creationDate = data.creationDate ? new DateFormat(data.creationDate).toDateTimeString() : '- - / - - / - - - -'
        this.updateDate = data.updateDate ? new DateFormat(data.updateDate).toDateTimeString() : '- - / - - / - - - -'
        this.disableDate = data.disableDate ? new DateFormat(data.disableDate).toDateTimeString() : '- - / - - / - - - -'
        if (!data.cellPhone) {
          data.cellPhone = { countryCode: null as any, number: null as any }
        }
        this.form = data

        this.initValues()

        this.$validator.reset()
      })
        .catch((error) => {
          let messageValues = {
            title: 'Error en la carga de usuarios',
            message: '',
            delay: 4000,
            error: error
          }
          new MessageError({ propsData: messageValues }).displayMessage()
          this.onCancel()
        })
        .finally(() => {
          this.loading = false
        })
    } else {
      const domainOfLogged = this.domains.find(it => it.id === userInfoStore.userInfo.domain.id) as BasicEntity
      this.selectDomain(domainOfLogged || { id: userInfoStore.userInfo.domain.id, name: userInfoStore.userInfo.domain.name })
      this.watchGroupsTreeActive = true
    }
  }

  async initValues () {
    if (this.form.domain) {
      const domain = this.domains.find(it => it.id === this.form.domain.id) as any
      await this.selectDomain(domain || this.form.domain)

      if (this.form.groups) {
        const selectedIds = this.form.groups.reduce((arr: number[], it: BasicEntity) => arr.concat(it.id), [])
        this.setGroupsSelected(this.groups, selectedIds)
        setTimeout(() => {
          this.watchGroupsTreeActive = true
        }, 500)
      }
    }

    if (!this.readOnly) {
      if (this.form.client) {
        const client = this.clients.find(it => it.id === this.form.client.id)
        setTimeout(() => {
          this.selectClient(client)
        }, 500)
      }

      if (this.form.role) {
        const role = this.roles.find(it => it.id === this.form.role.id)
        setTimeout(() => {
          this.selectRole(role)
        }, 500)
      }
    }

    if (this.form.cellPhone) {
      this.onCountryCodeChange(this.form.cellPhone.countryCode)
    }
  }

  /* User permissions */

  get isBmAdminWithPermission () {
    return this.isRootDomainUser && this.BMAdminWithPermissionRoles.includes(userInfoStore.userInfo.role.name)
  }

  get isClientWithPermission () {
    return userInfoStore.userInfo.role.name === RoleNames.CLIENT_GENERIC_ADMIN || userInfoStore.userInfo.role.name === RoleNames.CLIENT_OWNER
  }

  /** Checks if the user belongs to root domain */
  get isRootDomainUser () {
    return userInfoStore.inRootDomain()
  }

  getStatusText () {
    if (this.form.id) {
      switch (this.form.status) {
        case 'active':
          return 'Activo'
        case 'inactive':
          return 'Baja'
        case 'suspended':
          return 'Suspendido'
        case 'updatePassword':
          return 'Contraseña restablecida'
      }
    } else {
      return 'Activo'
    }
  }

  showDomainSelection (flag: boolean) {
    if (!this.isBmAdminWithPermission) return
    if (flag) {
      this.searchDomain = ''
      this.domainSelected = {}
      this.domainSelectionOpen = true
    } else {
      setTimeout(() => {
        this.domainSelectionOpen = false
        if (this.domainSelectedBackup.name && this.domainSelectedBackup.id !== this.domainSelected.id) {
          this.searchDomain = this.domainSelectedBackup.name
          this.domainSelected = this.domainSelectedBackup
        }
      }, 200)
    }
  }

  async selectDomain (domain: BasicEntity) {
    this.domainSelected = domain
    this.domainSelectedBackup = domain
    this.searchDomain = domain.name
    await this.loadGroupsByDomain(domain.id)
    if (this.readOnly) {
      if (this.form.client) {
        this.selectClient(this.form.client)
      }
      if (this.form.role) {
        this.selectRole(this.form.role)
      }
    } else {
      await this.getClientsBySelectedDomain()
      await this.getRolesBySelectedDomain()
    }
  }

  isMatchDomain (domain: any) {
    if (!this.searchDomain) return true
    const regexSearch = new RegExp(this.searchDomain, 'i')
    return regexSearch.test(domain.name)
  }

  async getClientsBySelectedDomain () {
    this.loadingClients = true
    this.clients = []
    this.clientSelected = this.clientSelectedBackup = {}
    this.searchClient = ''
    const pageRequest = new PageRequest<ClientWorkspacesFilters>()
    pageRequest.filters.domainId = this.domainSelected.id
    const response = await catalogsService.getWorkspacesByDomain(pageRequest)

    this.clients.push(...response.results.map((it: Workspace) => {
      return { id: it.client.id, code: it.client.clientCode, name: it.client.name, status: it.status } as any
    }))

    let clientToSelect: any = this.clients
      .find((it) =>
        userInfoStore.userInfo.client
          ? it.id === userInfoStore.userInfo.client.id
          : it.id === BLUEMESSAGING_ID)

    setTimeout(() => {
      this.loadingClients = false
      if (clientToSelect) {
        this.selectClient(clientToSelect)
      }
    }, 350)
  }

  showClientSelection (flag: boolean) {
    if (flag) {
      this.searchClient = ''
      this.clientSelected = {}
      this.clientSelectionOpen = true
    } else {
      setTimeout(() => {
        this.clientSelectionOpen = false
        if (this.clientSelectedBackup.code) {
          this.selectClient(this.clientSelectedBackup)
        }
      }, 200)
    }
  }

  selectClient (client: any) {
    this.clientSelected = client
    this.clientSelectedBackup = client
    this.searchClient = client.name
  }

  isMatchClient (client: any) {
    if (client.status !== UserStatus.ACTIVO) return false
    if (!this.searchClient) return true
    const regexSearch = new RegExp(this.searchClient, 'i')
    return regexSearch.test(client.name)
  }

  async getRolesBySelectedDomain () {
    this.loadingRoles = true
    this.roles = []
    this.roleSelected = this.roleSelectedBackup = {}
    this.searchRole = ''
    const pageRequest = new PageRequest<RoleCatalogFilters>()
    pageRequest.filters.domain = this.domainSelected.id
    const response = await catalogsService.getRoles(pageRequest)
    for (const role of response.results) {
      if (this.isRootDomainUser || !ADMIN_ROLES.includes(role.name as RoleNames)) {
        this.roles.push({ id: role.id, name: role.name } as any)
      }
    }
    this.loadingRoles = false
  }

  showRoleSelection (flag: boolean) {
    if (flag) {
      this.searchRole = ''
      this.roleSelected = {}
      this.roleSelectionOpen = true
    } else {
      setTimeout(() => {
        this.roleSelectionOpen = false
        if (this.roleSelectedBackup.id) {
          this.selectRole(this.roleSelectedBackup)
        }
      }, 200)
    }
  }

  selectRole (role: any) {
    this.roleSelected = role
    this.roleSelectedBackup = role
    this.searchRole = role.name
  }

  isMatchRole (role: any) {
    if (!this.searchRole) return true
    const regexSearch = new RegExp(this.searchRole, 'i')
    return regexSearch.test(role.name)
  }

  /**
   * Configurations for a page request to get the group records
   *
   * @returns a PageRequest<GroupCatalogFilters>
   */
  prepareGroupRequest (domainId: number) {
    let pageRequest = new PageRequest<GroupCatalogFilters>()
    pageRequest.sort = 'name'
    // Only when the user is root is require all the groups in the domain
    if (userInfoStore.inRootDomain()) {
      pageRequest.filters.domain = domainId
    }
    pageRequest.filters.status = GroupStatus.ACTIVE
    return pageRequest
  }

  /**
   * Gets the group records
   *
   * @param domainId from get groups
   */
  async loadGroupsByDomain (domainId: number) {
    let pageRequest = this.prepareGroupRequest(domainId)
    const response = await catalogsService.getGroups(pageRequest)
    if (response.total <= 0) {
      this.$bvToast.toast(
        'No existe ningún grupo en este dominio. Es necesario contar con al menos uno para relacionarlo con el usuario.',
        {
          'title': 'Dominio sin grupos',
          'solid': true,
          'variant': 'warning',
          'noAutoHide': true
        })
    }
    this.groups = response.results
  }

  onCountryCodeChange (countryCode: any) {
    const item = this.phoneCodes.find(it => it.value === countryCode)
    this.selectedCountryIso = item ? item.iso : ''
  }

  getGroupsSelected (groups: Group[]): BasicEntity[] {
    let groupsObjects: BasicEntity[] = []
    groups.forEach(it => {
      if (it._selected) groupsObjects.push(new BasicEntity(it.id, it.name))
      if (it.subGroups && it.subGroups.length) groupsObjects = groupsObjects.concat(this.getGroupsSelected(it.subGroups))
    })
    return groupsObjects
  }

  setGroupsSelected (groups: Group[], selectedIds: number[]) {
    groups.forEach(it => {
      if (selectedIds.indexOf(it.id) > -1) Vue.set(it, '_selected', true) // check a group that was selected
      else Vue.set(it, '_selected', false) // uncheck a group that wasn't selected
      if (it.subGroups && it.subGroups.length) this.setGroupsSelected(it.subGroups, selectedIds)
    })
  }

  /**
   * Get the current groups ids related to the user
   *
   * @returns an array of group ids
   */
  getGroupSelectedIds (): number[] {
    return this.form.groups
      .reduce(
        (arr: number[], group: BasicEntity) =>
          !arr.includes(group.id)
            ? arr.concat(group.id)
            : arr /* callback */,
        [] /* initialValue */
      )
  }

  /**
   * Update the current group related to the user when one of them was changed
   *
   * @param selected flag that indicates if the group was selected or not
   * @param group that was selected or not
   */
  updateGroupsSelected (selected: boolean, group: Group): void {
    // Current selected groups related to the user
    // this may have duplicated values
    const selectedIds = this.getGroupSelectedIds()
    let selectedIdsSet = new Set(selectedIds)
    // Add or remove the group id from the set
    if (selected) selectedIdsSet.add(group.id)
    else selectedIdsSet.delete(group.id)

    const uniqueSelectedIds = Array.from(selectedIdsSet)
    // Update the state of all the checkboxes in the tree of groups
    this.setGroupsSelected(this.groups, uniqueSelectedIds)
    // Set the groups selected as the groups of the user
    this.form.groups = this.getGroupsSelected(this.groups)
  }

  validateState (ref: any) {
    const thiz = this as any
    if (
      thiz.veeFields[ref] &&
      (thiz.veeFields[ref].dirty || thiz.veeFields[ref].validated)
    ) {
      return !thiz.veeErrors.has(ref)
    }
    return null
  }

  get validateClient () {
    return this.searchClient && this.searchClient.length > 0
  }

  /**
   * @returns true if the current form has groups selected, false otherwise
   */
  validatesGroups () {
    return this.form.groups && this.form.groups.length > 0
  }

  /**
   * Launchs a message to confirm is the user does need groups
   *
   * @returns a boolean promise
   */
  async userWithoutGroupConfirmation () : Promise<boolean> {
    let confirmation = await this.$bvModal.msgBoxConfirm(
      'El usuario que se intenta guardar no cuenta con un grupo relacionado. ¿Estás seguro de guardar los cambios?',
      {
        title: 'Guardar usuario',
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'primary',
        okTitle: 'Sí, guardar',
        cancelVariant: 'outline-secondary',
        cancelTitle: 'Cancelar',
        headerClass: 'p-2 border-bottom-0',
        footerClass: 'p-2 border-top-0',
        hideHeaderClose: false
      }
    )
    return confirmation as boolean
  }

  async onSubmit () {
    // Get the groups selected
    this.form.groups = this.getGroupsSelected(this.groups)
    if (!this.validatesGroups()) {
      let noGroupConfirmation = await this.userWithoutGroupConfirmation()
      if (!noGroupConfirmation) {
        return
      }
    }
    this.$validator.validateAll().then(result => {
      if (result) {
        if (!(this.domainSelected.id >= 0)) {
          document.getElementById('input-domain')!.focus()
          return false
        }

        if (!this.clientSelected.id) {
          document.getElementById('input-client')!.focus()
          return false
        }

        if (!this.roleSelected.id) {
          document.getElementById('input-role')!.focus()
          return false
        }

        this.form.domain = new BasicEntity(this.domainSelected.id, this.domainSelected.name)
        this.form.client = new BasicEntity(this.clientSelected.id, this.clientSelected.name)
        this.form.role = new BasicEntity(this.roleSelected.id, this.roleSelected.name)

        this.loading = true
        this.form.updateDate = new Date()
        catalogsService.saveUser(this.form).then((response: User) => {
          this.form.id = response.id
          this.$emit('saved', this.form)
        }).catch((error) => {
          messageService.error('Error al guardar el usuario', `${error.response!.data!.description} - ${error.response!.data!.message}`)
        }).finally(() => {
          this.loading = false
        })
      } else {
        messageService.error('Errores en el formulario', 'Revise los campos marcados con error', 4500)
      }
    })
  }

  inactiveUser () {
    this.$bvModal.msgBoxConfirm(`Una vez que el usuario se da de baja no es posible reactivarlo. ¿Desea continuar con la baja del usuario?`, {
      title: 'Confirmación',
      size: 'md',
      okVariant: 'danger',
      okTitle: 'Sí',
      cancelTitle: 'No',
      footerClass: 'p-2',
      hideHeaderClose: false,
      centered: true
    })
      .then(async (confirmed: boolean) => {
        if (confirmed) {
          await catalogsService.inactiveUser(this.id).then(() => {
            this.form.status = UserStatus.INACTIVO
            this.form.disableDate = new Date()
            this.$emit('saved', this.form)
          }).catch((error) => {
            messageService.error('Error al dar de baja el usuario', `${error.response!.data!.description} - ${error.response!.data!.message}`)
          })
        }
      })
  }

  resetPass () {
    this.$bvModal.msgBoxConfirm(`¿Desea aplicar un reseteo de password para el usuario?`, {
      title: 'Confirmación',
      size: 'md',
      okVariant: 'danger',
      okTitle: 'Sí',
      cancelTitle: 'No',
      footerClass: 'p-2',
      hideHeaderClose: false,
      centered: true
    })
      .then(async (confirmed: boolean) => {
        if (confirmed) {
          catalogsService.resetUserPassword(this.id).then(() => {
            this.form.status = UserStatus.UPDATE_PASSWORD
            this.form.updateDate = new Date()
            this.$emit('saved', this.form)
          }).catch((error) => {
            messageService.error('Error al actualizar el usuario', `${error.response!.data!.description} - ${error.response!.data!.message}`)
          })
        }
      })
  }

  onEdit () {
    this.$emit('edit')
  }

  onCancel () {
    this.$emit('close')
  }
}
